import { Deferred, toAsyncResult } from "./utils";
import { CustomProperties, UserProfile } from "../common/types";
import { Office } from "../common";

let messageId = 0;

type Result =
  | {
      type: "Error";
      reason: any;
    }
  | {
      type: "Success";
      payload: any;
    };

type Callback = (result: Result) => void;

interface RequestMessage {
  messageId: string;
  operation: string;
  payload?: any;
}

export interface ResponseMessage {
  correlatedId: string;
  result: Result;
}

export class BaseWebviewInterop {
  private callbacks = new Map<string, Callback>();

  private toMessage(operation, payload: any): RequestMessage {
    return {
      messageId: `${messageId}`,
      operation,
      payload,
    };
  }

  constructor() {
    window.chrome.webview?.addEventListener("message", (evt) => {
      const resp = evt.data as ResponseMessage;

      const cb = this.callbacks.get(resp.correlatedId);

      if (!cb) return;

      cb(resp.result);

      this.callbacks.delete(resp.correlatedId);
    });
  }

  public async request<T = void>(operation: string, payload?: any) {
    const deferred = new Deferred<T>();

    this.callbacks.set(`${messageId}`, (result) => {
      if (result.type == "Error") {
        deferred.reject(result.reason);
      } else {
        deferred.resolve(result.payload);
      }
    });

    window.chrome.webview?.postMessage(this.toMessage(operation, payload));
    messageId++;

    return deferred.promise;
  }
}

export class VstoInterop extends BaseWebviewInterop {
  async getUserProfile() {
    return await this.request<UserProfile>("getUserProfile");
  }

  async getCurrentItemSubject() {
    return await this.request<string>("mailbox.item.subject.get");
  }

  async setCurrentItemSubject(subject: string) {
    return await this.request("mailbox.item.subject.set", subject);
  }

  async getCurrentItemBody(coercionType: string) {
    return await this.request<string>("mailbox.item.body.get", coercionType);
  }

  async setCurrentItemBody(data: string, coercionType: string) {
    return await this.request("mailbox.item.body.set", { data, coercionType });
  }

  async setCurrentItemBodySelectedData(data: string, coercionType: string) {
    return await this.request("mailbox.item.body.setSelectedData", { data, coercionType });
  }

  async setCurrentItemLocation(location: string) {
    return await this.request("mailbox.item.location.set", location);
  }

  async loadCustomPropertiesAsync() {
    const rawProps = await this.request<UserProp[]>("mailbox.item.loadCustomProperties");
    return new XCustomProperties(rawProps);
  }

  async saveCustomPropertiesAsync(customProperties: UserProp[]) {
    return await this.request("mailbox.item.saveCustomProperties", customProperties);
  }
}

export interface UserProp {
  name: string;
  value: any;
}

export class XCustomProperties implements CustomProperties {
  private currentValues: Map<string, any> = new Map();

  constructor(currentValues: UserProp[]) {
    currentValues.forEach((x) => this.currentValues.set(x.name, x.value));
  }

  get(name: string): any {
    return this.currentValues.get(name);
  }

  getAll(): any {}

  remove(name: string): void {
    this.currentValues.delete(name);
  }

  saveAsync(callback?: (asyncResult: Office.AsyncResult<void>) => void): void {
    const items: UserProp[] = [];

    this.currentValues.forEach((value, key) =>
      items.push({
        name: key,
        value: value,
      })
    );

    toAsyncResult(useVstoInterop().saveCustomPropertiesAsync(items)).then(callback);
  }

  set(name: string, value: string): void {
    this.currentValues.set(name, value);
  }
}

let vstoInterop: VstoInterop | null = null;

export function useVstoInterop() {
  if (!vstoInterop) {
    vstoInterop = new VstoInterop();
  }

  return vstoInterop;
}
